// Private Provider Mock API Service
// Simulates backend API calls with realistic data and latency

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Mock data generators
const generateId = () => Math.random().toString(36).substr(2, 9).toUpperCase();

const cities = [
  "Duval County",
  "St Johns County",
  "Clay County",
  "Nassau County",
];
const contractors = [
  { id: "C001", name: "ABC Construction", license: "CBC012345" },
  { id: "C002", name: "Best Builders LLC", license: "CBC054321" },
  { id: "C003", name: "Prime Contracting", license: "CBC098765" },
];

const addresses = [
  "123 Oak Street, Jacksonville, FL 32207",
  "456 Pine Avenue, St Augustine, FL 32084",
  "789 Maple Drive, Green Cove Springs, FL 32043",
  "321 Cedar Lane, Fernandina Beach, FL 32034",
];

// Storage for workbench page data - keeps page states persistent
const workbenchPageData = {};

// Submission seed data
const mockSubmissions = [
  {
    id: "PRS001",
    permitNumber: "PKG2024001",
    address: addresses[0],
    contractor: contractors[0],
    packageTier: "Residential Standard",
    city: cities[0],
    type: "residential",
    status: "in_review",
    dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
    dueBucket: "75%",
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    assignedReviewer: "John Smith, PE",
    notes: "Standard residential building permit review",
    files: [
      {
        id: "PRF001" + "-01-00",
        name: "Architectural Plans",
        type: "pdf",
        size: "2.5MB",
        uploadedAt: new Date().toISOString(),
      },
      {
        id: "PRF001" + "-02-00",
        name: "Structural Drawings",
        type: "pdf",
        size: "1.8MB",
        uploadedAt: new Date().toISOString(),
      },
    ],
    workbench: {
      totalPages: 15,
      reviewedPages: 8,
      compliantPages: 6,
      needsCorrectionPages: 2,
      naPages: 0,
    },
  },
  {
    id: "PRS002",
    permitNumber: null,
    address: addresses[1],
    contractor: contractors[1],
    packageTier: "Commercial Premium",
    city: cities[1],
    type: "commercial",
    status: "pending",
    dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
    dueBucket: "50%",
    createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    assignedReviewer: null,
    notes: "Commercial building - fast track review requested",
    files: [
      {
        id: "PRF001" + "-01-00",
        name: "Site Plans",
        type: "pdf",
        size: "3.2MB",
        uploadedAt: new Date().toISOString(),
      },
    ],
    workbench: null,
  },
  {
    id: "PRS003",
    permitNumber: "PKG2024003",
    address: addresses[2],
    contractor: contractors[2],
    packageTier: "Residential Basic",
    city: cities[2],
    type: "residential",
    status: "corrections_needed",
    dueDate: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString(),
    dueBucket: "25%",
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    assignedReviewer: "Jane Doe, PE",
    notes: "Returned for corrections - structural issues identified",
    files: [
      {
        id: "PRF001" + "-01-01",
        name: "Architectural Plans (Revised)",
        type: "pdf",
        size: "2.7MB",
        uploadedAt: new Date().toISOString(),
      },
    ],
    workbench: {
      totalPages: 12,
      reviewedPages: 12,
      compliantPages: 8,
      needsCorrectionPages: 4,
      naPages: 0,
    },
  },
  {
    id: "PRS004",
    permitNumber: "PKG2024004",
    address: addresses[3],
    contractor: contractors[0],
    packageTier: "Commercial Premium",
    city: cities[3],
    type: "commercial",
    status: "approved",
    dueDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    dueBucket: "past_due",
    createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    assignedReviewer: "Mike Johnson, AIA",
    notes: "Commercial project - approved with minor conditions",
    files: [
      {
        id: "PRF004" + "-01-00",
        name: "Site Development Plan",
        type: "pdf",
        size: "4.2MB",
        uploadedAt: new Date().toISOString(),
      },
      {
        id: "PRF004" + "-02-00",
        name: "Architectural Drawings",
        type: "pdf",
        size: "3.8MB",
        uploadedAt: new Date().toISOString(),
      },
      {
        id: "PRF004" + "-03-00",
        name: "Structural Engineering",
        type: "pdf",
        size: "2.1MB",
        uploadedAt: new Date().toISOString(),
      },
      {
        id: "PRF004" + "-04-00",
        name: "MEP Plans",
        type: "pdf",
        size: "5.5MB",
        uploadedAt: new Date().toISOString(),
      },
    ],
    workbench: {
      totalPages: 24,
      reviewedPages: 24,
      compliantPages: 22,
      needsCorrectionPages: 0,
      naPages: 2,
    },
  },
];

// Inspection templates and data
const mockInspectionTemplates = [
  {
    id: "IT001",
    name: "Standard Residential",
    inspections: [
      {
        id: "I001",
        name: "Footing/Foundation",
        required: true,
        dependencies: [],
      },
      { id: "I002", name: "Slab", required: true, dependencies: ["I001"] },
      { id: "I003", name: "Framing", required: true, dependencies: ["I002"] },
      {
        id: "I004",
        name: "Mechanical Rough-In",
        required: true,
        dependencies: ["I003"],
      },
      {
        id: "I005",
        name: "Electrical Rough-In",
        required: true,
        dependencies: ["I003"],
      },
      {
        id: "I006",
        name: "Plumbing Rough-In",
        required: true,
        dependencies: ["I003"],
      },
      {
        id: "I007",
        name: "Insulation",
        required: true,
        dependencies: ["I004", "I005", "I006"],
      },
      { id: "I008", name: "Drywall", required: false, dependencies: ["I007"] },
      { id: "I009", name: "Final", required: true, dependencies: ["I008"] },
    ],
  },
  {
    id: "IT002",
    name: "Commercial Basic",
    inspections: [
      { id: "I010", name: "Foundation", required: true, dependencies: [] },
      {
        id: "I011",
        name: "Structural Steel",
        required: true,
        dependencies: ["I010"],
      },
      {
        id: "I012",
        name: "Fire Suppression",
        required: true,
        dependencies: ["I011"],
      },
      {
        id: "I013",
        name: "Final Commercial",
        required: true,
        dependencies: ["I012"],
      },
    ],
  },
];

// Finance data
const mockPayouts = [
  {
    id: "PO001",
    month: "2024-01",
    totalAmount: 2850.0,
    ppShare: 855.0, // 30%
    status: "paid",
    paidDate: "2024-02-01",
    services: [
      { type: "Plan Review", count: 8, amount: 2400.0 },
      { type: "Inspection", count: 15, amount: 450.0 },
    ],
  },
  {
    id: "PO002",
    month: "2024-02",
    totalAmount: 3200.0,
    ppShare: 960.0,
    status: "pending",
    paidDate: null,
    services: [
      { type: "Plan Review", count: 10, amount: 3000.0 },
      { type: "Inspection", count: 8, amount: 200.0 },
    ],
  },
];

// Notifications
const mockNotifications = [
  {
    id: "N001",
    type: "submission",
    title: "New Submission Assigned",
    message: "PRS001 has been assigned to you for review",
    read: false,
    createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
  },
  {
    id: "N002",
    type: "reminder",
    title: "Submission Due Soon",
    message: "PRS003 is due for review in 2 hours",
    read: false,
    createdAt: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
  },
];

// Settings data
const mockSettings = {
  notarization: {
    providesOfficeNotarization: true,
    ronEnabled: true,
    ipenEnabled: true,
    ipenDeviceMode: "same_device", // 'same_device' | 'different_devices'
  },
  cityProfiles: cities.map((city, index) => ({
    id: `CITY${String(index + 1).padStart(3, "0")}`,
    name: city,
    portalLink: `https://${city.toLowerCase().replace(/\s+/g, "")}.gov/permits`,
    autoReport: true,
    requiredFields: ["permit_number", "address", "contractor_license"],
  })),
  payoutShare: 30, // percentage
};

export const ppMockApi = {
  // Submissions API
  async getSubmissions(filters = {}) {
    await sleep(300);
    // Create deep copies to avoid state mutation
    let filtered = mockSubmissions.map((submission) => ({
      ...submission,
      workbench: submission.workbench ? { ...submission.workbench } : null,
      contractor: { ...submission.contractor },
    }));

    if (filters.contractor) {
      filtered = filtered.filter((s) =>
        s.contractor.name
          .toLowerCase()
          .includes(filters.contractor.toLowerCase())
      );
    }

    if (filters.status) {
      filtered = filtered.filter((s) => s.status === filters.status);
    }

    if (filters.dueBucket) {
      filtered = filtered.filter((s) => s.dueBucket === filters.dueBucket);
    }

    // Return deep copies to prevent mutations
    return filtered.map((submission) => ({
      ...submission,
      files:
        submission.files?.map((file) => ({
          id: file.id,
          name: file.name,
          type: file.type,
          size: file.size,
          uploadedAt: file.uploadedAt,
        })) || [],
      contractor: submission.contractor ? { ...submission.contractor } : null,
    }));
  },

  async createSubmission(submissionData) {
    await sleep(500);

    // Deep clone the submission data to avoid mutations
    const cleanSubmissionData = {
      ...submissionData,
      files:
        submissionData.files?.map((file) => ({
          id: file.id,
          name: file.name,
          type: file.type,
          size: file.size,
          uploadedAt: file.uploadedAt,
          // Don't include the actual File object to prevent mutations
        })) || [],
      contractor: submissionData.contractor
        ? { ...submissionData.contractor }
        : null,
    };

    const newSubmission = {
      id: `PRS${String(mockSubmissions.length + 1).padStart(3, "0")}`,
      ...cleanSubmissionData,
      status: "pending",
      createdAt: new Date().toISOString(),
      workbench: null,
    };
    mockSubmissions.push(newSubmission);
    return newSubmission;
  },

  async updateSubmission(id, updates) {
    await sleep(300);
    const index = mockSubmissions.findIndex((s) => s.id === id);
    if (index !== -1) {
      // Deep clone to avoid mutations
      const updatedSubmission = {
        ...mockSubmissions[index],
        ...updates,
        files: updates.files
          ? updates.files.map((file) => ({
              id: file.id,
              name: file.name,
              type: file.type,
              size: file.size,
              uploadedAt: file.uploadedAt,
            }))
          : mockSubmissions[index].files,
        contractor: updates.contractor
          ? { ...updates.contractor }
          : mockSubmissions[index].contractor,
      };
      mockSubmissions[index] = updatedSubmission;
      return updatedSubmission;
    }
    throw new Error("Submission not found");
  },

  async assignReviewer(submissionId, reviewerName) {
    await sleep(300);
    return this.updateSubmission(submissionId, {
      assignedReviewer: reviewerName,
      status: "in_review",
    });
  },

  async sendReminder(submissionId) {
    await sleep(200);
    // Mock sending reminder notification
    return { success: true, message: "Reminder sent successfully" };
  },

  // Workbench API
  async getWorkbench(submissionId) {
    await sleep(400);
    const submission = mockSubmissions.find((s) => s.id === submissionId);
    if (!submission) throw new Error("Submission not found");

    // Initialize or get existing page data for this submission
    if (!workbenchPageData[submissionId]) {
      // Generate initial pages based on submission status
      const totalPages = submission.workbench?.totalPages || 15;
      const pages = [];

      for (let i = 0; i < totalPages; i++) {
        let status;
        let comments = [];
        let issues = [];

        if (submission.status === "approved") {
          // For approved submissions, all pages should be compliant
          status = "compliant";
        } else if (submission.status === "corrections_needed") {
          // For corrections needed, mix of compliant and needs_correction
          status = i % 2 === 0 ? "compliant" : "needs_correction";
          if (status === "needs_correction") {
            comments = [
              {
                id: "COM001",
                text: "Foundation detail needs clarification per IBC 1805.4.2",
                author: "Jane Doe, PE",
                timestamp: new Date().toISOString(),
                codeSection: "IBC 1805.4.2",
              },
            ];
            issues = ["Structural"];
          }
        } else {
          // For in_review or pending, use pattern showing some work done (75%)
          if (i < 8) {
            status = i % 2 === 0 ? "compliant" : "needs_correction";
            if (status === "needs_correction") {
              comments = [
                {
                  id: "COM001",
                  text: "Foundation detail needs clarification per IBC 1805.4.2",
                  author: "John Smith, PE",
                  timestamp: new Date().toISOString(),
                  codeSection: "IBC 1805.4.2",
                },
              ];
              issues = ["Structural"];
            }
          } else {
            status = "not_reviewed";
          }
        }

        pages.push({
          id: i + 1,
          documentName:
            i < 5 ? "Architectural" : i < 8 ? "Structural" : "Electrical",
          pageName: `A${i + 1}.${Math.floor(i / 3) + 1}`,
          status,
          comments,
          issues,
        });
      }

      workbenchPageData[submissionId] = pages;
    }

    return {
      submission: {
        ...submission,
        workbench: submission.workbench ? { ...submission.workbench } : null,
        contractor: { ...submission.contractor },
      },
      pages: workbenchPageData[submissionId].map((page) => ({ ...page })),
    };
  },

  async savePageCheck(
    submissionId,
    pageId,
    status,
    comments = [],
    issues = []
  ) {
    await sleep(200);

    // Update the persistent page data
    if (workbenchPageData[submissionId]) {
      const pageIndex = workbenchPageData[submissionId].findIndex(
        (page) => page.id === pageId
      );

      if (pageIndex !== -1) {
        workbenchPageData[submissionId][pageIndex] = {
          ...workbenchPageData[submissionId][pageIndex],
          status,
          comments,
          issues,
        };

        // Update the submission's workbench stats
        const submission = mockSubmissions.find((s) => s.id === submissionId);
        if (submission && submission.workbench) {
          const pages = workbenchPageData[submissionId];
          // Create a new workbench object to avoid mutation
          submission.workbench = {
            ...submission.workbench,
            totalPages: pages.length,
            reviewedPages: pages.filter((p) => p.status !== "not_reviewed")
              .length,
            compliantPages: pages.filter((p) => p.status === "compliant")
              .length,
            needsCorrectionPages: pages.filter(
              (p) => p.status === "needs_correction"
            ).length,
            naPages: pages.filter((p) => p.status === "not_applicable").length,
          };
        }
      }
    }

    return { success: true, pageId, status, comments, issues };
  },

  async approveSubmission(submissionId, notes = "") {
    await sleep(500);

    // Validate that all pages are reviewed and no corrections needed
    if (workbenchPageData[submissionId]) {
      const pages = workbenchPageData[submissionId];
      const reviewedPages = pages.filter(
        (p) => p.status !== "not_reviewed"
      ).length;
      const needsCorrectionPages = pages.filter(
        (p) => p.status === "needs_correction"
      ).length;

      if (reviewedPages < pages.length) {
        throw new Error("Cannot approve: Not all pages have been reviewed");
      }

      if (needsCorrectionPages > 0) {
        throw new Error("Cannot approve: Some pages still need corrections");
      }

      // Mark all pages in persistent storage as approved status
      workbenchPageData[submissionId] = workbenchPageData[submissionId].map(
        (page) => {
          if (page.status === "not_reviewed") {
            return { ...page, status: "compliant" };
          }
          return page;
        }
      );
    }

    return this.updateSubmission(submissionId, {
      status: "approved",
      approvedAt: new Date().toISOString(),
      approvalNotes: notes,
    });
  },

  async returnForCorrections(submissionId, notes) {
    await sleep(500);
    return this.updateSubmission(submissionId, {
      status: "corrections_needed",
      correctionNotes: notes,
      returnedAt: new Date().toISOString(),
    });
  },

  // Utility function to mark all pages as compliant
  async markAllPagesCompliant(submissionId) {
    await sleep(300);

    if (workbenchPageData[submissionId]) {
      const pages = workbenchPageData[submissionId];

      // Create new page objects instead of mutating existing ones
      workbenchPageData[submissionId] = pages.map((page) => {
        if (page.status === "not_reviewed" || page.status === "compliant") {
          return { ...page, status: "compliant" };
        }
        return page; // Keep pages that need corrections unchanged
      });

      // Update the submission's workbench stats
      const submission = mockSubmissions.find((s) => s.id === submissionId);
      if (submission && submission.workbench) {
        const updatedPages = workbenchPageData[submissionId];
        // Create a new workbench object to avoid mutation
        submission.workbench = {
          ...submission.workbench,
          totalPages: updatedPages.length,
          reviewedPages: updatedPages.filter((p) => p.status !== "not_reviewed")
            .length,
          compliantPages: updatedPages.filter((p) => p.status === "compliant")
            .length,
          needsCorrectionPages: updatedPages.filter(
            (p) => p.status === "needs_correction"
          ).length,
          naPages: updatedPages.filter((p) => p.status === "not_applicable")
            .length,
        };
      }

      return {
        success: true,
        message: "All eligible pages marked as compliant",
      };
    }

    return { success: false, message: "No workbench data found" };
  },

  // Inspections API
  async getInspectionTemplates() {
    await sleep(300);
    return mockInspectionTemplates;
  },

  async saveInspectionTemplate(template) {
    await sleep(400);
    if (template.id) {
      const index = mockInspectionTemplates.findIndex(
        (t) => t.id === template.id
      );
      if (index !== -1) {
        mockInspectionTemplates[index] = template;
        return template;
      }
    } else {
      const newTemplate = {
        ...template,
        id: `IT${String(mockInspectionTemplates.length + 1).padStart(3, "0")}`,
      };
      mockInspectionTemplates.push(newTemplate);
      return newTemplate;
    }
  },

  async getPermitInspections(permitId) {
    await sleep(300);
    // Mock inspection data for a permit
    return [
      {
        id: "INS001",
        name: "Footing/Foundation",
        status: "passed",
        scheduledDate: new Date(
          Date.now() - 2 * 24 * 60 * 60 * 1000
        ).toISOString(),
        completedDate: new Date(
          Date.now() - 2 * 24 * 60 * 60 * 1000
        ).toISOString(),
        inspector: "Mike Johnson",
        photos: ["photo1.jpg", "photo2.jpg"],
        comments: "Foundation meets code requirements",
      },
      {
        id: "INS002",
        name: "Framing",
        status: "pending",
        scheduledDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        completedDate: null,
        inspector: "Mike Johnson",
        photos: [],
        comments: "",
      },
    ];
  },

  async transitionInspection(permitId, inspectionId, action, data = {}) {
    await sleep(400);
    // Mock inspection transition (approve, fail, pre-approve, etc.)
    return {
      success: true,
      inspectionId,
      action,
      newStatus:
        action === "approve"
          ? "approved"
          : action === "fail"
            ? "failed"
            : "pending",
      ...data,
    };
  },

  async getInspectionsSummary() {
    await sleep(300);
    return {
      total: 45,
      pending: 12,
      approved: 28,
      failed: 5,
      preApproved: 8,
    };
  },

  // Finance API
  async getPayouts(dateRange = {}) {
    await sleep(300);
    // Return deep copies to avoid accidental mutations downstream
    return mockPayouts.map((p) => ({
      ...p,
      services: p.services ? p.services.map((s) => ({ ...s })) : [],
    }));
  },

  async markPayoutPaid(payoutId) {
    await sleep(300);
    const payout = mockPayouts.find((p) => p.id === payoutId);
    if (payout) {
      // Create a new object instead of mutating the existing one
      const updatedPayout = {
        ...payout,
        status: "paid",
        paidDate: new Date().toISOString(),
      };

      // Update the mockPayouts array with the new object
      const payoutIndex = mockPayouts.findIndex((p) => p.id === payoutId);
      if (payoutIndex !== -1) {
        mockPayouts[payoutIndex] = updatedPayout;
      }

      return updatedPayout;
    }
    throw new Error("Payout not found");
  },

  async getStatements() {
    await sleep(300);
    return [
      {
        id: "ST001",
        month: "2024-01",
        totalRevenue: 2850.0,
        ppShare: 855.0,
        services: {
          planReview: { count: 8, revenue: 2400.0, ppShare: 720.0 },
          inspections: { count: 15, revenue: 450.0, ppShare: 135.0 },
        },
        status: "finalized",
      },
      {
        id: "ST002",
        month: "2024-02",
        totalRevenue: 3200.0,
        ppShare: 960.0,
        services: {
          planReview: { count: 10, revenue: 3000.0, ppShare: 900.0 },
          inspections: { count: 8, revenue: 200.0, ppShare: 60.0 },
        },
        status: "draft",
      },
    ];
  },

  async getFinanceSummary() {
    await sleep(300);
    const currentMonth = new Date().getMonth() + 1;
    return {
      monthlyEarnings: 960.0, // Current month earnings
      pendingPayouts: 960.0,
      totalEarnings: 1815.0, // YTD
      avgMonthlyRevenue: 907.5,
    };
  },

  // Notifications API
  async getNotifications() {
    await sleep(200);
    return mockNotifications;
  },

  async markNotificationRead(notificationId) {
    await sleep(200);
    const notification = mockNotifications.find((n) => n.id === notificationId);
    if (notification) {
      notification.read = true;
      return notification;
    }
    throw new Error("Notification not found");
  },

  // Settings API
  async getPpSettings() {
    await sleep(300);
    return mockSettings;
  },

  async savePpSettings(settings) {
    await sleep(400);
    Object.assign(mockSettings, settings);
    return mockSettings;
  },

  // Messaging API (for future implementation)
  async getConversations() {
    await sleep(300);
    return []; // Placeholder for messaging functionality
  },

  async getMessages(conversationId) {
    await sleep(300);
    return []; // Placeholder
  },

  async sendMessage(conversationId, message) {
    await sleep(400);
    return { success: true }; // Placeholder
  },
};
